import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { PermissionGuard } from '@components/PermissionGuard';
import { AcquirerTab } from './tabs/AcquirerTab';
import { AcquirerCreditCardConfigTab } from './tabs/AcquirerCreditCardConfigTab';
import { AcquirerPIXConfigTab } from './tabs/AcquirerPIXConfigTab';
import { AcquirerCardTaxTab } from './tabs/AcquirerCardTaxTab';
import { AcquirerPIXTaxTab } from './tabs/AcquirerPIXTaxTab';
import { PaymentDefinitionTab } from './tabs/PaymentDefinitionTab';
import { PaymentRoutingTab } from './tabs/PaymentRoutingTab';
import { CieloWebhookTab } from './tabs/CieloWebhookTab';

const TABS = [
  { key: 'acquirers', label: 'Adquirentes' },
  { key: 'credit-card-config', label: 'Config. Cartão de Crédito' },
  { key: 'pix-config', label: 'Config. PIX' },
  { key: 'card-taxes', label: 'Taxas de Cartão' },
  { key: 'pix-taxes', label: 'Taxas PIX' },
  { key: 'payment-definitions', label: 'Definição de Pagamentos' },
  { key: 'payment-routing', label: 'Roteamento de Pagamentos' },
  { key: 'cielo-webhook', label: 'Notificações da Cielo' },
] as const;

type TabKey = typeof TABS[number]['key'];

const isTabKey = (value: string | null): value is TabKey =>
  TABS.some((t) => t.key === value);

export const ReceivingHubView: React.FC = () => {
  // A aba fica na URL para que outras telas consigam apontar direto para ela —
  // o painel de certificados do dashboard leva a ?tab=pix-config. Com a aba só
  // em estado local, o link cairia sempre em "Adquirentes".
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = isTabKey(tabParam) ? tabParam : 'acquirers';

  const setActiveTab = (key: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    // replace: trocar de aba não é navegação — não deve encher o histórico
    // e fazer o "voltar" percorrer as abas uma a uma.
    setSearchParams(next, { replace: true });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'acquirers':
        return <AcquirerTab />;
      case 'credit-card-config':
        return <AcquirerCreditCardConfigTab />;
      case 'pix-config':
        return <AcquirerPIXConfigTab />;
      case 'card-taxes':
        return <AcquirerCardTaxTab />;
      case 'pix-taxes':
        return <AcquirerPIXTaxTab />;
      case 'payment-definitions':
        return <PaymentDefinitionTab />;
      case 'payment-routing':
        return <PaymentRoutingTab />;
      case 'cielo-webhook':
        return <CieloWebhookTab />;
    }
  };

  return (
    <PermissionGuard permission="receiving_hub.read" showError>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-h1">Hub de Recebimentos</h1>
          <p className="text-slate-600">
            Gerencie adquirentes, configurações de recebimento e taxas.
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <nav className="-mb-px flex space-x-6 overflow-x-auto" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </PermissionGuard>
  );
};
